// ADDED: Salary Advance Bracket Hook
import { useState, useCallback, useEffect } from "react";
import { useGeneralMessage } from "../../common/APICommon";
import { SetAuditTrail, SetAuditTrailBulk } from "../../components/Reports/AuditTrailReportEmployeeSetup";
import salaryAdvanceDeductionBracketService from "../services/salaryAdvanceDeductionBracketService";


export const useSalaryAdvanceDeductionBracket = () => {
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState([]);
  const [filterData, setFilterData] = useState([]);

  const { showGeneralMessage, showMessage, message, isSuccess } = useGeneralMessage();

  const fetchBrackets = useCallback(async () => {
    setLoading(true);
    try {
      const res = await salaryAdvanceDeductionBracketService.getAll();
      const responseData = res.data || [];
      setData(responseData);
      setFilterData(responseData);
    } catch (err) {
      console.error("Failed to fetch brackets", err);
      showGeneralMessage("Failed to load salary advance brackets", false);
    } finally {
      setLoading(false);
    }
  }, [showGeneralMessage]);

  useEffect(() => {
    fetchBrackets();
  }, [fetchBrackets]);

  // MODIFIED: Fetch specific bracket by ID with minimum 500ms delay to prevent flicker
  const getBracketById = async (id) => {
    const startTime = Date.now(); // ADDED: Record start time
    setLoading(true);
    try {
      const res = await salaryAdvanceDeductionBracketService.getById(id);

      // ADDED: Calculate elapsed time and wait if it's less than 500ms
      const elapsed = Date.now() - startTime;
      if (elapsed < 300) {
        await new Promise(resolve => setTimeout(resolve, 300 - elapsed));
      }

      return { success: true, data: res.data };
    } catch (err) {
      console.error("Failed to fetch bracket details", err);
      showGeneralMessage("Failed to load bracket details", false);
      return { success: false };
    } finally {
      setLoading(false);
    }
  };

  const getDeductionAmountByCutoffCount = async ({employeePersonalInformationId, loanAmount, payrollPeriodId, deductionFrequency}) => {
    setLoading(true);

    try {
      const params = {
        employeePersonalInformationId: employeePersonalInformationId,
        loanAmount: loanAmount,
        payrollPeriodId: payrollPeriodId,
        deductionFrequency: deductionFrequency
      };
      const res = await salaryAdvanceDeductionBracketService.getDeductionAmountByCutoffCount(params);
      if (!res?.deductionAmount) {
        showGeneralMessage("Please set the deduction bracket for the employee's employee type", false);
        return false;
      }
      return res;
    } catch (err) {
      console.error("Failed to fetch bracket details", err);
      showGeneralMessage("Failed to load bracket details", false);
      return { success: false };
    } finally {
      setLoading(false);
    }
  };

  const handleAction = async (actionFn, auditData, ...args) => {
    setLoading(true);
    try {
      const res = await actionFn(...args);

      if (Array.isArray(auditData)) {
        await SetAuditTrailBulk({ data: auditData });
      } else if (auditData) {
        await SetAuditTrail(auditData);
      }

      showGeneralMessage(res.data?.message || "Action successful", true);
      await fetchBrackets();
      return { success: true, data: res.data };
    } catch (err) {
      const errorMsg = err.response?.data?.message || "An error occurred";
      showGeneralMessage(errorMsg, false);
      return { success: false };
    } finally {
      setLoading(false);
    }
  };

  const searchData = (value) => {
    if (!value) {
      setData(filterData);
      return;
    }
    const lower = value.toLowerCase();
    setData(filterData.filter(f =>
      f.employeeTypeDescription?.toLowerCase().includes(lower) ||
      f.id?.toString().includes(lower)
    ));
  };

  return {
    loading, setLoading,
    data,
    showMessage,
    message,
    isSuccess,
    searchData,
    getBracketById,
    getDeductionAmountByCutoffCount,
    createBracket: (p, audit) => handleAction(salaryAdvanceDeductionBracketService.create, audit, p),
    updateBracket: (id, p, audit) => handleAction(salaryAdvanceDeductionBracketService.update, audit, id, p),
    deleteBracket: (id) => handleAction(salaryAdvanceDeductionBracketService.delete, null, id),
    refresh: fetchBrackets
  };
};